import ListCard from '@Components/ListCard/ListCard';
import DeleteLocationModal from '@Components/Modals/DeleteLocation/DeleteLocationModal';
import LocationModal from '@Components/Modals/Location/LocationModal';
import LocationServices from '@Services/Location.services.ts';
import { Location } from '@Types/Asset.types';
import { PaginatedResponse } from '@Types/Response.types';
import { PAGE_PROP, PAGE_SIZE, PAGINATED_DEFAULT, PATHS, SEARCH_PROP, TABLE_STRUCTURES } from '@Utils/constants';
import { handleRequestError } from '@Utils/request.utils';
import { Button, Icons, Pagination, SearchBox, SectionCard, useTopHat } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const Locations = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [locations, setLocations] = useState<PaginatedResponse<Location>>(PAGINATED_DEFAULT);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [showLocationModal, setShowLocationModal] = useState<boolean>(false);
  const [showDeleteLocationModal, setShowDeleteLocationModal] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | undefined>(undefined);
  const currentPage = Number(searchParams.get(PAGE_PROP) ?? '1');
  const searchQuery = searchParams.get(SEARCH_PROP) ?? '';

  useTopHat({
    activeNav: 'nav-settings',
    activeTab: undefined,
    tabs: [],
    breadcrumbs: [
      { title: 'Settings', href: PATHS.SETTINGS, id: 'Settings' },
      {
        title: 'Locations',
        href: `${PATHS.LOCATIONS}?page=${1}`,
        id: 'Locations',
      },
    ],
  });

  const handleNewPage = (newPage: number, data: PaginatedResponse<Location>) => {
    if (newPage <= 0 || newPage > locations.totalPages) {
      return;
    }
    const page = newPage > data.totalPages ? data.prevPage : newPage;
    if (page !== currentPage) {
      searchParams.set(PAGE_PROP, page?.toString() || '1');
      setSearchParams(searchParams);
    }
  };

  const getLocations = () => {
    setSelectedLocation(undefined);
    setIsLoading(true);
    LocationServices.searchLocation({ search: searchQuery }, currentPage)
      .then(({ data }) => {
        setLocations(data);
        setErrorMessage(undefined);
        handleNewPage(currentPage, data);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    getLocations();
  }, [searchQuery, currentPage]);

  const handleSearchQueryChange = (newValue: string) => {
    searchParams.set(SEARCH_PROP, newValue);
    searchParams.set(PAGE_PROP, '1');
    if (!newValue) {
      searchParams.delete(SEARCH_PROP);
    }
    setSearchParams(searchParams);
  };

  const handleActionLocation = (id: string, setModal: (show: boolean) => void) => {
    setSelectedLocation(locations.docs.find((doc) => doc.id === id));
    setModal(true);
  };

  return (
    <div className='grid'>
      <div className='col-12 col-md-3'>
        <SectionCard>
          <SearchBox
            id='search-locations'
            fullWidth
            focusOnRender
            value={searchQuery}
            defaultValue={searchQuery}
            onChange={handleSearchQueryChange}
          />
          <Button type='primary' className='button-with-icon' fullWidth onClick={() => setShowLocationModal(true)}>
            {Icons.Plus} Add new Location
          </Button>
        </SectionCard>
      </div>
      <div className='col-12 col-md-9'>
        <ListCard<Location>
          title={'Locations'}
          structure={TABLE_STRUCTURES.LOCATIONS}
          data={locations.docs}
          loading={isLoading}
          withActions={{
            edit: (id: string) => handleActionLocation(id, setShowLocationModal),
            delete: (id: string) => handleActionLocation(id, setShowDeleteLocationModal),
          }}
          error={errorMessage}
          searchEntered={!!searchQuery}
        />
      </div>
      <div className='col-12 content-middle content-right-md'>
        <Pagination
          count={locations.totalDocs}
          perPage={PAGE_SIZE}
          currentPage={currentPage - 1}
          onChange={(page) => handleNewPage(page + 1, locations)}
        />
      </div>
      <LocationModal
        show={showLocationModal}
        onClose={() => {
          setShowLocationModal(false);
          setSelectedLocation(undefined);
        }}
        onSuccess={() => getLocations()}
        location={selectedLocation}
      />
      <DeleteLocationModal
        show={showDeleteLocationModal}
        onClose={() => {
          setShowDeleteLocationModal(false);
          setSelectedLocation(undefined);
        }}
        onSuccess={() => getLocations()}
        location={selectedLocation}
      />
    </div>
  );
};

export default Locations;
